import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const Dashboard = ({ roomId = '1' }) => {
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  useEffect(() => {
    const fetchDeviceStatus = async (showLoading = false) => {
      try {
        if (showLoading) setLoading(true);
        setError(null);
        if (!baseUrl) {
          throw new Error("REACT_APP_API_BASE_URL is not defined in the environment.");
        }
        const response = await fetch(`${baseUrl}/room/${roomId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setDeviceStatus(data);
      } catch (e) {
        setError("Unable to load device data: " + e.message);
        console.error("Error retrieving data:", e);
      } finally {
        if (showLoading) setLoading(false);
      }
    };

    fetchDeviceStatus(true); // Show loading only on first fetch
    const intervalId = setInterval(() => fetchDeviceStatus(false), 5000); // No loading on interval
    return () => clearInterval(intervalId);
  }, [roomId, baseUrl]);

  if (loading) {
    return <div className="dashboard-container">Loading...</div>;
  }

  if (error) {
    return <div className="dashboard-container error-message">{error}</div>;
  }

  if (!deviceStatus) {
    return <div className="dashboard-container">No data available.</div>;
  }

  const { thermostat, curtains, lights } = deviceStatus;

  return (
    <div className="dashboard-container">
      <h1>Dashboard Room: {roomId}</h1>

      <div className="card-grid">
        {/* Thermostat Card */}
        <div className="card thermostat-card">
          <h2>Thermostat</h2>
          <div className="thermostat-display">
            {thermostat && thermostat.temperature !== null ? (
              <>
                <div className="thermostat-dial">
                  <div className="thermostat-fill" style={{ height: `${(thermostat.temperature - 10) * 100 / 20}%` }}></div>
                  <span className="thermostat-temp">{thermostat.temperature}°C</span>
                </div>
                <p className="status-text">Status: On</p>
              </>
            ) : (
              <p className="status-text">N/A</p>
            )}
          </div>
        </div>

        {/* Curtains Card */}
        <div className="card curtains-card">
          <h2>Curtains</h2>
          <div className="window-container">
            <div className="window-view"></div>
            <div
              className="curtains"
              style={{ height: `${100 - (curtains?.percentage || 0)}%` }}
            ></div>
            <p className="curtains-percentage">
              {/* Clear label for curtains: "Opening: X%" */}
              {curtains && curtains.percentage !== null ? (
                `Percentage: ${curtains.percentage}%`
              ) : (
                'Status: N/A'
              )}
            </p>
          </div>
        </div>

        {/* Lights Card */}
        <div className="card lights-card">
          <h2>Lights</h2>
          <div className="lights-grid">
            {/* Room Lights */}
            <div className="light-item">
              <div className={`light-icon ${lights?.room?.on ? 'on' : 'off'}`}></div>
              <p>Room</p>
              <span className="light-status-text">
                {lights?.room?.on === true ? 'On' : lights?.room?.on === false ? 'Off' : 'N/A'}
              </span>
              {lights?.room?.on && lights.room.brightness !== null && (
                <span className="brightness-text">{lights.room.brightness}%</span>
              )}
              {/* Show "N/A" if the light doesn't have a valid brightness value even though it's on */}
              {lights?.room?.on && lights.room.brightness === null && (
                <span className="brightness-text">N/A</span>
              )}
            </div>

            {/* Entrance light */}
            <div className="light-item">
              <div className={`light-icon ${lights?.entrance?.on ? 'on' : 'off'}`}></div>
              <p>Entrance</p>
              <span className="light-status-text">
                {lights?.entrance?.on === true ? 'On' : lights?.entrance?.on === false ? 'Off' : 'N/D'}
              </span>
              {/* Le luci dell'ingresso non hanno luminosità, quindi non mostriamo nulla qui se non il loro stato base */}
            </div>

            {/* Luci Bathroom */}
            <div className="light-item">
              <div className={`light-icon ${lights?.bathroom?.on ? 'on' : 'off'}`}></div>
              <p>Bathroom</p>
              <span className="light-status-text">
                {lights?.bathroom?.on === true ? 'On' : lights?.bathroom?.on === false ? 'Off' : 'N/D'}
              </span>
              {lights?.bathroom?.on && lights.bathroom.brightness !== null && (
                <span className="brightness-text">{lights.bathroom.brightness}%</span>
              )}
              {/* Mostra "N/D" se la luce non ha un valore di luminosità valido pur essendo accesa */}
              {lights?.bathroom?.on && lights.bathroom.brightness === null && (
                <span className="brightness-text">N/D</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;